import { useState, useEffect } from "react"
import styled from "styled-components"
import { NextSeo } from "next-seo"
import Link from "next/link"
import type { GetStaticPropsContext } from "next"

import { getSite } from "@lib/site"
import { getDictionary } from "@lib/dictionary"
import { getAllProducts } from "@lib/product"
import { getColorValue } from "@lib/utils"

import { useCustomer } from "@hooks/index"
import { FluidImage } from "@components/common"
import {
  ProductThumbnailTextWrapper,
  ProductThumbnailFirstVariant,
} from "@components/ui"

import { AuthContainer, AccountNav, Title, Placeholder } from "@components/account"
import { ContentfulProduct, Site } from "@lib/types"

export default function VirtualCloset({
  products,
  site,
}: {
  products: ContentfulProduct[]
  site: Site
}) {
  const { customer } = useCustomer()

  const orders = customer?.orders?.edges
  const fulfilledOrders = orders?.filter(
    ({ node }: { node: any }) => node.fulfillmentStatus === "FULFILLED"
  )

  const lineItems = fulfilledOrders
    ?.flatMap(({ node }: { node: any }) =>
      node.lineItems.edges.map(({ node }: { node: any }) => node)
    )
    ?.filter(
      (v: any, i: number, a: []) =>
        a.findIndex(({ title }: { title: string }) => title === v.title) === i
    )
    ?.filter(({ variant }: { variant: {} }) => !!variant)

  const mergedData = lineItems
    ?.map((lineItem: any) => {
      const idx = products.findIndex(
        ({ entryTitle }: { entryTitle: string }) =>
          entryTitle === lineItem.title
      )
      if (idx === -1) return null
      const contentfulProduct = products[idx]

      return {
        ...lineItem,
        ...contentfulProduct,
      }
    })
    .filter((x: any) => x)

  const [displayItems, setDisplayItems] = useState([])

  useEffect(() => {
    setDisplayItems(mergedData)
  }, [])

  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const itemsToDisplay = mergedData?.filter(
      ({ entryTitle }: { entryTitle: string }) =>
        entryTitle.toLowerCase().includes(searchQuery.toLowerCase())
    )

    setDisplayItems(itemsToDisplay)
  }, [searchQuery])

  return (
    <>
      <NextSeo title="Virtual Closet" />
      <AuthContainer>
        <Body>
          <AccountNav />
          <StyledVirtualCloset>
            <Header>
              <Title>Virtual Closet</Title>
              {displayItems?.length > 0 && (
                <Search>
                  <svg
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M9 17A8 8 0 109 1a8 8 0 000 16z"
                      stroke="#24211E"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M19 19l-4.35-4.35"
                      stroke="#24211E"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </Search>
              )}
            </Header>
            {displayItems?.length > 0 ? (
              <Grid>
                {displayItems?.map((item: any) => {
                  return (
                    <GridItem key={item.variant.id}>
                      <Thumbnail>
                        <Link href={`/products/${item.slug}`} passHref>
                          <a>
                            <StyledFluidImage
                              src={item.variant.image.originalSrc}
                            />
                          </a>
                        </Link>
                        <ProductThumbnailTextWrapper>
                          <h1>{item.title}</h1>
                          <ProductThumbnailFirstVariant>
                            {item?.color?.name ||
                              getColorValue(item.variant.selectedOptions)}{" "}
                            | <span>{`$${+item.variant.priceV2.amount}`}</span>
                          </ProductThumbnailFirstVariant>
                        </ProductThumbnailTextWrapper>
                      </Thumbnail>
                    </GridItem>
                  )
                })}
              </Grid>
            ) : (
              <Placeholder richText={site.virtualClosetPlaceholder} />
            )}
          </StyledVirtualCloset>
        </Body>
      </AuthContainer>
    </>
  )
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 14rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 0 15rem;
    flex-direction: row;
    padding: 7.2rem 12rem 7rem 9rem;
  }
`

const StyledVirtualCloset = styled.section`
  width: 100%;
  max-width: 86.8rem;
  padding: 2.1rem 2rem 0;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding: 0;
  }
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 3.5rem;
`

const Search = styled.div`
  display: flex;

  input {
    border: none;
    font-family: ${({ theme }) => theme.font.primary};
    width: 6rem;
    font-size: 1.5rem;
    line-height: 3rem;
  }

  svg {
    margin: 0 1.1rem 0 0;
    width: 1.8rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    input {
      transition: width 0.2s ease-out;

      &:focus {
        width: 18rem;
      }
    }
  }
`

const Grid = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 2.2rem 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 6.4rem 2.8rem;
  }
`

const GridItem = styled.li`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    width: 100%;
  }
`

const Thumbnail = styled.div``

const StyledFluidImage = styled(FluidImage)`
  margin: 0 0 1.5rem;
  width: 100%;
  height: 24rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 0 2.7rem;
    height: 41.5rem;
  }
`

export async function getStaticProps({
  preview = false,
  locale = "en-US",
}: GetStaticPropsContext) {
  const site = await getSite({ preview, locale })
  const dictionary = await getDictionary({ preview, locale })
  const products = await getAllProducts()

  return {
    props: {
      site,
      dictionary,
      isAccountPage: true,
      products,
      locale,
    },
    revalidate: 900,
  }
}
